import React from "react"
import { twMerge } from "tailwind-merge"

import { sanitize } from "../../../utils/utils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import {
  formatFontFamily,
  formatHeadingFontFamily,
  getParagraphFontWeight,
  stripWysiwygInlineStylesFromHtml
} from "../utils/fontUtils"
import { applyButtonTheme, getButtonClasses, getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildBookingUrl, buildPublicPageUrl, openExternalUrl } from "../utils/urlUtils"
import { getWysiwygScopeId, getWysiwygScopeSelector } from "../utils/wysiwygScopeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollRevealItem } from "./shared/ScrollReveal"

const AboutSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)
  const { themeColors } = colorScheme
  const bgMediumColor = themeColors.bgMedium || "#E6DAD7"

  const layout = settings.layout || "image_left"
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Learn more"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""

  // Use color scheme for text colors
  const headingColor = colorScheme.headingColor
  const bodyColor = colorScheme.bodyColor
  const sectionBackgroundColor = colorScheme.sectionBackground
  const linkColor = colorScheme.linkColor
  const buttonStyles = colorScheme.primaryButton
  const wysiwygScope = getWysiwygScopeSelector(section.id, "about")
  const wysiwygScopeId = getWysiwygScopeId(section.id, "about")

  const resolveWysiwygHtml = (value) => {
    if (!value) return ""

    // Some models store rich text as { html, delta }
    if (typeof value === "object") {
      return value.html || ""
    }

    if (typeof value === "string") {
      const trimmed = value.trim()
      if (!trimmed) return ""

      // Handle stringified JSON: {"html":"...","delta":{...}}
      if (trimmed.startsWith("{") && trimmed.includes('"html"')) {
        try {
          const parsed = JSON.parse(trimmed)
          if (parsed && typeof parsed === "object" && typeof parsed.html === "string") {
            return parsed.html
          }
        } catch (_) {
          // Not JSON, treat as plain HTML string
        }
      }

      // If it looks like plain text (no tags), preserve newlines
      if (!trimmed.includes("<")) {
        return trimmed.replace(/\n/g, "<br />")
      }

      return value
    }

    return String(value)
  }

  const bodyHtml = sanitize(stripWysiwygInlineStylesFromHtml(resolveWysiwygHtml(section.body)))

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        openExternalUrl(ctaTarget)
        break
      default:
        break
    }
  }

  const renderContent = () => (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-6">
        {section.headline && (
          <ScrollRevealItem staggerIndex={0}>
            <h2
              className="text-[40px] font-bold leading-[48px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: headingColor
              }}>
              {section.headline}
            </h2>
          </ScrollRevealItem>
        )}
        {section.body && (
          <ScrollRevealItem staggerIndex={1}>
            <>
              <style>{`
              ${wysiwygScope} {
                text-align: initial !important;
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
                font-weight: ${getParagraphFontWeight(
                  theme?.fonts?.paragraph,
                  theme?.fonts?.paragraphRegularWeight
                )} !important;
              }
              ${wysiwygScope} p,
              ${wysiwygScope} li,
              ${wysiwygScope} span,
              ${wysiwygScope} div:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6),
              ${wysiwygScope} *:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6):not(strong):not(b):not(em):not(i):not(u) {
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
                font-weight: ${getParagraphFontWeight(
                  theme?.fonts?.paragraph,
                  theme?.fonts?.paragraphRegularWeight
                )} !important;
              }
              ${wysiwygScope} ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              ${wysiwygScope} .ql-align-right {
                text-align: right !important;
              }
              ${wysiwygScope} .ql-align-center {
                text-align: center !important;
              }
              ${wysiwygScope} .ql-align-justify {
                text-align: justify !important;
              }
              ${wysiwygScope} .ql-align-left {
                text-align: left !important;
              }
              ${wysiwygScope} a {
                color: ${linkColor} !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} a:hover {
                opacity: 0.8 !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:first-child {
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:last-child {
                margin-bottom: 0 !important;
              }
              ${wysiwygScope} strong,
              ${wysiwygScope} b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
                color: inherit !important;
              }
              ${wysiwygScope} em,
              ${wysiwygScope} i {
                font-style: italic !important;
              }
              ${wysiwygScope} u {
                text-decoration: underline !important;
              }
              ${wysiwygScope} h1,
              ${wysiwygScope} h2,
              ${wysiwygScope} h3,
              ${wysiwygScope} h4,
              ${wysiwygScope} h5,
              ${wysiwygScope} h6 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                color: ${bodyColor} !important;
              }
              ${wysiwygScope} h1 *,
              ${wysiwygScope} h2 *,
              ${wysiwygScope} h3 *,
              ${wysiwygScope} h4 *,
              ${wysiwygScope} h5 *,
              ${wysiwygScope} h6 * {
                color: inherit !important;
              }
              ${wysiwygScope} h1 {
                font-size: 56px !important;
                line-height: 64px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
              }
              ${wysiwygScope} h2 {
                font-size: 40px !important;
                line-height: 48px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
              }
              ${wysiwygScope} h3 {
                font-size: 28px !important;
                line-height: 32px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
              }
              ${wysiwygScope} h4 {
                font-size: 20px !important;
                line-height: 24px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
              }
              ${wysiwygScope} h5 {
                font-size: 18px !important;
                line-height: 28px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
              ${wysiwygScope} h6 {
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
              @media (max-width: 768px) {
                ${wysiwygScope} h1 {
                  font-size: 40px !important;
                  line-height: 48px !important;
                }
                ${wysiwygScope} h2 {
                  font-size: 28px !important;
                  line-height: 32px !important;
                }
                ${wysiwygScope} h3 {
                  font-size: 24px !important;
                  line-height: 28px !important;
                }
                ${wysiwygScope} h4 {
                  font-size: 18px !important;
                  line-height: 24px !important;
                }
                ${wysiwygScope} h5 {
                  font-size: 16px !important;
                  line-height: 24px !important;
                }
                ${wysiwygScope} h6 {
                  font-size: 14px !important;
                  line-height: 20px !important;
                }
              }
            `}</style>
              <div
                id={wysiwygScopeId}
                className="about-section-content wysiwyg-content max-w-none text-[20px] leading-[32px]" // eslint-disable-line tailwindcss/no-custom-classname
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  fontWeight: getParagraphFontWeight(theme?.fonts?.paragraph, theme?.fonts?.paragraphRegularWeight),
                  color: bodyColor
                }}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            </>
          </ScrollRevealItem>
        )}
      </div>

      {showCTA && (
        <ScrollRevealItem staggerIndex={2}>
          <button
            onClick={handleCTAClick}
            className={twMerge(
              "inline-flex w-fit items-center justify-center px-10 py-3 text-base font-normal leading-[24px] transition-all hover:shadow-lg",
              getButtonClasses(theme?.buttonStyle)
            )}
            style={applyButtonTheme(
              {
                backgroundColor: buttonStyles.backgroundColor,
                color: buttonStyles.color,
                fontFamily: formatFontFamily(theme?.fonts?.paragraph)
              },
              theme
            )}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = buttonStyles.hoverBackgroundColor
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = buttonStyles.backgroundColor
            }}>
            {ctaText}
          </button>
        </ScrollRevealItem>
      )}
    </div>
  )

  // Desktop-specific layouts with complex grid system
  switch (layout) {
    case "image_left":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <div className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex flex-row items-start gap-[104px]">
              {section.imageUrl ? (
                <div className="relative w-auto shrink-0">
                  {/* Desktop: Complex grid layout */}
                  <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start leading-[0]">
                    <div
                      className={twMerge("[grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        marginLeft: "272px",
                        marginTop: "346px"
                      }}
                    />
                    <div
                      className={twMerge("bg-cover bg-center [grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        width: "464px",
                        height: "568px",
                        backgroundImage: `url(${section.imageUrl})`,
                        backgroundColor: bgMediumColor
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="relative w-auto shrink-0">
                  {/* Desktop: Complex layout */}
                  <div className="relative" style={{ width: "464px", height: "568px" }}>
                    <div
                      className="absolute"
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        left: "272px",
                        top: "346px"
                      }}
                    />
                    <div className="relative flex size-full items-center justify-center bg-gray-200">
                      <span className="text-gray-500">No image selected</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent()}</div>
            </div>
          </div>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <div className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex flex-row items-start gap-[104px]">
              {section.imageUrl ? (
                <div className="relative order-2 w-auto shrink-0">
                  {/* Desktop: Complex grid layout */}
                  <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start leading-[0]">
                    <div
                      className={twMerge("[grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        marginLeft: "272px",
                        marginTop: "346px"
                      }}
                    />
                    <div
                      className={twMerge("bg-cover bg-center [grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        width: "464px",
                        height: "568px",
                        backgroundImage: `url(${section.imageUrl})`,
                        backgroundColor: bgMediumColor
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="relative order-2 w-auto shrink-0">
                  {/* Desktop: Complex layout */}
                  <div className="relative" style={{ width: "464px", height: "568px" }}>
                    <div
                      className="absolute"
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        left: "272px",
                        top: "346px"
                      }}
                    />
                    <div className="relative flex size-full items-center justify-center bg-gray-200">
                      <span className="text-gray-500">No image selected</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="order-1 w-full flex-1">{renderContent()}</div>
            </div>
          </div>
        </section>
      )

    case "centered":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-14",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <div className="mx-auto max-w-4xl px-16 text-center">
            {section.imageUrl && (
              <div className="mb-8 flex justify-center">
                <div className="relative">
                  <div
                    className={twMerge("absolute -bottom-6 -right-6 h-24 w-20", getThemeShapeClasses(theme))}
                    style={{ backgroundColor: theme?.colors?.accent || "#E6DAD7" }}
                  />
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "About image"}
                    className={twMerge("relative size-64 object-cover", getThemeShapeClasses(theme))}
                  />
                </div>
              </div>
            )}
            {renderContent()}
          </div>
        </section>
      )

    case "no_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-14",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <div className="mx-auto max-w-4xl px-16">{renderContent()}</div>
        </section>
      )
  }
}

export default AboutSectionDesktop
